<script lang="ts">
    import { cn } from '$lib/utils/cn';

    import get from 'lodash/get';
    import { afterUpdate } from 'svelte';
    import Dropzone from 'svelte-file-dropzone';

    import StatusIcon from './StatusIcon.svelte';
    import Emoji from '../../_wrappers/Emoji.svelte';
    import { resolveInputStyling } from '../_utils/resolveInputStyling';
    import translations from '../translations';

    import type { ErrorCode, FileDropEvent } from '$lib/types/Dropzone';
    import type { Language } from '$lib/types/Language';
    import type { Theme } from '$lib/types/Theme';

    export let placeholder = '';
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let finalFormInput: {
        onChange: (value: string) => void;
        onFocus: () => void;
        onBlur: () => void;
    } = {
        onChange: () => {},
        onFocus: () => {},
        onBlur: () => {},
    };
    export let onFileAccept: (file: File) => void = () => null;
    export let uploading = false;
    export let errorMessage = '';
    export let language: Language = 'en';
    export let focusColor = '';
    export let outlineColor = '';
    export let theme: Theme | undefined = undefined;

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = false;
    let showLoading = uploading;
    let rejectReason: ErrorCode | undefined;
    let dragHovered = false;
    let inputElementContainer: HTMLDivElement | undefined;

    $: {
        // this will detect when the focusable element in the dropzone has been focused or blurred
        // like this we can use final form input's way of detecting if the input is touched
        if (inputElementContainer) {
            const inputElements =
                inputElementContainer.getElementsByClassName('inner-input-container');

            if (inputElements.length === 1) {
                inputElements[0].addEventListener('focus', finalFormInput.onFocus);
                inputElements[0].addEventListener('blur', finalFormInput.onBlur);
            }
        }
    }

    const rejectReasonMap: Record<ErrorCode, string> = {
        'file-invalid-type': translations[language].validation.InvalidFile,
        'file-too-large': translations[language].validation.FileTooLarge,
        'too-many-files': translations[language].validation.TooManyFiles,
    };

    let wrapperClasses: string;
    let inputClasses: string;
    let dropzoneClasses: string;

    afterUpdate(() => {
        showError = invalid && touched;
        showSuccess = !invalid && !pristine;
        showCheckmark = !uploading;
        showLoading = uploading;
    });

    let fileName = '';

    const handleFileDrop = (event: FileDropEvent) => {
        const { acceptedFiles, fileRejections } = event.detail;
        dragHovered = false;

        if (acceptedFiles.length) {
            const file = acceptedFiles[0];
            fileName = get(file, 'name', '');

            // to resolve "required" validation
            finalFormInput.onChange(fileName);
            invalid = false;
            rejectReason = undefined;

            return onFileAccept(file);
        }

        if (fileRejections.length) {
            const rejection = fileRejections[0];
            fileName = get(rejection, 'file.name', '');
            rejectReason = get(rejection, 'errors[0].code', undefined);

            invalid = true;

            // This will mark the input as touched
            finalFormInput.onFocus();
            finalFormInput.onBlur();
        }
    };

    const handleDragEnter = () => {
        dragHovered = true;
    };

    const handleDragLeave = () => {
        dragHovered = false;
    };

    const {
        borderRadiusClass,
        shadowClass,
        borderColor,
        backgroundColor,
        textColor,
        mutedTextColor,
    } = resolveInputStyling({
        theme,
        shadowComponent: 'fileInput',
    });

    const isThemeV2 = !!backgroundColor;
    const dropzoneRadiusClass = isThemeV2 ? borderRadiusClass : 'fun-rounded-md';

    $: wrapperClasses = cn(
        'fun-relative fun-box-border fun-flex fun-items-center fun-bg-white',
        borderRadiusClass,
        shadowClass,
        {
            ['fun-bg-gray-50']: dragHovered,
        },
    );

    $: inputClasses = cn(
        'fun-box-border fun-min-h-[90px] fun-w-full fun-cursor-pointer fun-border fun-p-2 fun-align-middle fun-font-ui fun-text-base fun-ring-2 fun-ring-transparent fun-transition-all',
        'fun-group-focus:fun-border-[var(--focusColor)] fun-group-focus:fun-ring-[var(--outlineColor)]',
        borderRadiusClass,
        {
            ['fun-h-[1px]']: !invalid || !showError,
            ['fun-h-[117px]']: invalid && showError,
        },
    );

    $: dropzoneClasses = cn(
        'fun-flex fun-size-full fun-items-center fun-border fun-border-dashed',
        dropzoneRadiusClass,
        {
            ['fun-py-4 fun-pl-12 fun-pr-4']: showEmoji,
            ['fun-p-4']: !showEmoji,
            ['fun-pr-12']: invalid,
        },
    );
</script>

<div
    class={wrapperClasses}
    bind:this={inputElementContainer}
    style:background-color={!dragHovered ? backgroundColor : undefined}
>
    {#if showEmoji}
        <u
            class="fun-absolute fun-left-6 fun-top-7 fun-flex fun-h-[18px] fun-select-none fun-items-center fun-no-underline"
            aria-hidden="true"
        >
            <Emoji {emoji} size="20px" />
        </u>
    {/if}

    <Dropzone
        ariaLabel={placeholder}
        on:drop={handleFileDrop}
        on:dragenter={handleDragEnter}
        on:dragleave={handleDragLeave}
        on:filedialogcancel={() => {
            // This will mark the input as touched
            finalFormInput.onFocus();
            finalFormInput.onBlur();
        }}
        disableDefaultStyles
        containerClasses="fun-group fun-w-full focus:fun-outline-none"
        maxSize={25_000_000}
        multiple={false}
        accept="image/png,image/jpeg,.pdf"
        disabled={uploading}
        {required}
    >
        <div
            class={inputClasses}
            style="--outlineColor: {outlineColor}; --focusColor: {focusColor}"
            style:border-color={borderColor}
        >
            <div
                class={dropzoneClasses}
                class:noEmoji={!showEmoji}
                style:border-color={borderColor}
            >
                <div class="fun-w-full">
                    <p
                        class="fun-overflow-hidden fun-truncate fun-text-sm"
                        style:color={fileName ? textColor : mutedTextColor}
                        class:fun-text-gray-900={!mutedTextColor}
                    >
                        {fileName ? fileName : placeholder}
                    </p>
                    {#if rejectReason || (errorMessage && showError)}
                        <div
                            class="fun-my-2 fun-font-ui fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600"
                        >
                            {rejectReason ? rejectReasonMap[rejectReason] : errorMessage}
                        </div>
                    {/if}
                    <p
                        class="fun-mt-1 fun-text-xs"
                        style:color={mutedTextColor}
                        class:fun-text-gray-500={!mutedTextColor}
                    >
                        ({!required ? 'optional, ' : ''}max. 25MB, .pdf, .png, .jpg)
                    </p>
                </div>
                <StatusIcon {showCheckmark} {showError} {showSuccess} {showLoading} isFileInput />
            </div>
        </div>
    </Dropzone>
</div>
